import type { ChatMessage } from './api';

/**
 * How a chat reads (Phase 124).
 *
 * A team channel is not a list of messages; it is a conversation, and the difference is entirely in
 * the grouping. Six lines from the same colleague in one minute is one thing somebody said, and
 * drawing six avatars, six names and six timestamps for it makes a room look like a log file.
 *
 * Pure, because the rules are product decisions with edges — a day that turns, a person who says
 * something an hour later, a message with no author — and each of those is a line somebody will
 * eventually look at and say "that is wrong".
 */

/** Messages from one person closer together than this belong to the same thing they said. */
export const GROUP_WITHIN_MINUTES = 5;

export interface MessageGroup {
  /** The author of every message in the run; null for a message the system wrote. */
  authorId: string | null;
  /** When the run started, which is the time shown above it. */
  at: string;
  messages: ChatMessage[];
}

export interface DaySection {
  /** `YYYY-MM-DD` in the reader's own zone, which is what a date divider means. */
  day: string;
  groups: MessageGroup[];
}

function dayOf(iso: string): string {
  const at = new Date(iso);

  // The reader's zone, not UTC: "ontem" is about the day the person had, and a message sent at
  // 22:30 in São Paulo belongs to that evening rather than to the next morning in London.
  return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(
    at.getDate(),
  ).padStart(2, '0')}`;
}

function minutesBetween(a: string, b: string): number {
  return Math.abs(new Date(a).getTime() - new Date(b).getTime()) / 60_000;
}

/**
 * The history as days, and each day as runs of consecutive messages by one person.
 *
 * Order is taken as given — the server returns oldest first and the live socket appends — so this
 * never re-sorts. A list that sorted here would hide a server that started returning the wrong
 * order, which is a bug worth seeing.
 */
export function groupMessages(messages: readonly ChatMessage[]): DaySection[] {
  const sections: DaySection[] = [];

  for (const message of messages) {
    const day = dayOf(message.created_at);
    let section = sections.at(-1);

    if (!section || section.day !== day) {
      section = { day, groups: [] };
      sections.push(section);
    }

    const group = section.groups.at(-1);
    const last = group?.messages.at(-1);

    const sameRun =
      group !== undefined &&
      last !== undefined &&
      group.authorId === message.author_id &&
      minutesBetween(last.created_at, message.created_at) <= GROUP_WITHIN_MINUTES;

    if (sameRun) {
      group.messages.push(message);
      continue;
    }

    section.groups.push({
      authorId: message.author_id,
      at: message.created_at,
      messages: [message],
    });
  }

  return sections;
}

/**
 * What a date divider says.
 *
 * "Hoje" and "Ontem" rather than a date, because that is how somebody reads a conversation they
 * were part of; anything older gets the date, because "quinta" is ambiguous after a week.
 */
export function dayLabel(day: string, now = new Date()): 'TODAY' | 'YESTERDAY' | 'DATE' {
  const today = dayOf(now.toISOString());

  if (day === today) return 'TODAY';

  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  return day === dayOf(yesterday.toISOString()) ? 'YESTERDAY' : 'DATE';
}
